"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { openUpgradeEntry } from "@/lib/trial/upgrade-entry-client";
import { Coins } from "lucide-react";
import { cn } from "@/lib/utils";
import { CardEntryModal } from "@/components/card-entry-modal";
import {
  CardFirstTrialModal,
  type CardFirstTrialTerms,
} from "@/components/trial/card-first-trial-modal";
import { startCardFirstTrialCheckout } from "@/lib/trial/card-first-client";
import { RatedCreditsModal } from "@/components/billing/rated/rated-credits-modal";
import type { Screen } from "@/components/billing/rated/rated-credits-flow";
import { useCheckoutIntentResume } from "@/lib/billing/use-checkout-intent-resume";
import { useEntitlementSyncing } from "@/lib/billing/use-entitlement-sync";
import {
  clearCheckoutIntent,
  type CheckoutIntent,
} from "@/lib/billing/checkout-intent-stash";
import type { PaidPlanId } from "@/lib/plans";
import { isRatedPaidPlan } from "@/lib/billing/credit-rates";
import {
  isUpgradeCopyVariant,
  type UpgradeCopyVariant,
} from "@/lib/billing/upgrade-copy-experiment";
import {
  isCardCopyVariant,
  coerceCardCopyVariant,
  type CardCopyVariant,
} from "@/lib/billing/card-copy-experiment";
import { trackUpgradeDismissed, trackCreditCopyMismatch } from "@/lib/analytics";
import { deriveCreditView, formatCreditBadge } from "@/lib/credits/display";
import { formatCreditHold } from "@/lib/credits/hold";
import { reportCreditCopyMismatches } from "@/lib/credits/copy-integrity";
import { formatMonthlyAllocation } from "@/lib/credits/monthly-allocation";

interface CreditCounterProps {
  /** Initial credit values (skip initial fetch if provided) */
  initialCredits?: number;
  initialTotal?: number;
  initialPlan?: string;
  initialResetsAt?: string | null;
  initialDailyUsed?: number;
  /** Free-tier daily spend cap (50); null for paid/expired users. */
  initialDailyLimit?: number | null;
  initialHasCard?: boolean;
  /** Credits an unsettled build is holding, when the caller already knows.
   *  Seeded like the other initial* values so a chip that skips the fetch can
   *  still explain a mid-build dip. */
  initialCreditsOnHold?: number;
  /** Card-copy A/B arm (GT-15) from a caller that already read /api/credits.
   *  Matters because the fetch below is skipped when initialCredits is seeded. */
  initialCardCopyVariant?: string | null;
  /** Subscription's monthly credit allocation (paid only; null for free). */
  initialMonthlyAllocation?: number | null;
  /** Agent engine counters (migration 044) */
  initialAgentTasksToday?: number;
  initialAgentDailyCap?: number;
  initialAgentDailyResetsAt?: string | null;
  /** Called when credits change (so parent can stay in sync) */
  onCreditsChange?: (credits: number, plan: string, resetsAt: string | null) => void;
  /** Project ID for upgrade/topup flow */
  projectId?: string;
}

/** Chip copy while a purchase is confirming. Desktop, then the narrow variant. */
const SYNCING_LABEL = "Confirming your purchase…";
const SYNCING_SHORT = "Confirming…";

export function CreditCounter({
  initialCredits,
  initialTotal,
  initialPlan,
  initialResetsAt,
  initialDailyUsed,
  initialDailyLimit,
  initialHasCard,
  initialCreditsOnHold,
  initialCardCopyVariant,
  initialMonthlyAllocation,
  initialAgentTasksToday,
  initialAgentDailyCap,
  initialAgentDailyResetsAt,
  onCreditsChange,
  projectId,
}: CreditCounterProps) {
  // True while a purchase is being confirmed anywhere in the app — a checkout
  // return waiting for the entitlement, or the post-conversion poll. Until it
  // clears, every number below is the PRE-purchase account: this component read
  // /api/credits once on mount and the grant has not happened yet. Rendering it
  // as fact is what told someone who had just paid that they had 50/50 free
  // credits, for the ~30s the confirmation took.
  const syncing = useEntitlementSyncing();
  const [credits, setCredits] = useState(initialCredits ?? 0);
  // Seeded to 0, NOT to 50. 50 is FREE_DAILY_CAP — it is not any plan's pool, and
  // seeding it here put a fabricated denominator behind every unresolved read: a
  // Pro account whose /api/credits call failed rendered "0/50 credits" while
  // Billing (a different endpoint) correctly said "Pro · 300 credits/month". That
  // was the reported bug. 0 makes formatCreditBadge drop the denominator entirely
  // (`hasMeaningfulLimit`), so an unknown ceiling reads as "0 credits" — honest —
  // rather than as a specific number the user does not have.
  const [total, setTotal] = useState(initialTotal ?? 0);
  const [plan, setPlan] = useState(initialPlan ?? "free");
  const [resetsAt, setResetsAt] = useState<string | null>(initialResetsAt ?? null);
  const [rollover, setRollover] = useState(0);
  const [dailyUsed, setDailyUsed] = useState(initialDailyUsed ?? 0);
  const [dailyLimit, setDailyLimit] = useState<number | null>(
    initialDailyLimit ?? null,
  );
  const [hasCard, setHasCard] = useState(initialHasCard ?? false);
  // Card-first wall (PANDA-2341): non-null while the server says the balance
  // cannot be spent until a card is on file. The chip then offers the trial
  // instead of quoting credits the pre-flight would refuse.
  const [cardFirstTrial, setCardFirstTrial] = useState<CardFirstTrialTerms | null>(null);
  const [cardFirstOpen, setCardFirstOpen] = useState(false);
  const [monthlyAllocation, setMonthlyAllocation] = useState<number | null>(
    initialMonthlyAllocation ?? null,
  );
  // Credits an unsettled build is holding. `credits` is already net of it, so
  // this is never subtracted — it is the reason the chip can dip mid-build and
  // recover, which the chip previously showed as an unexplained drop.
  //
  // Deliberately DERIVED rather than mirrored into state by a prop-sync effect
  // like its neighbours above: null means "no server value yet", so the seeded
  // prop stays live until a fetch or a credit-update event supplies one, and a
  // prop change is picked up with no effect at all. (An eleventh sync effect in
  // this component also trips react-hooks/set-state-in-effect for the whole
  // file — the existing ten are grandfathered; a new one is not.)
  const [fetchedOnHold, setFetchedOnHold] = useState<number | null>(null);
  const creditsOnHold = fetchedOnHold ?? initialCreditsOnHold ?? 0;
  // Card-copy A/B arm (GT-15). DERIVED for the same two reasons as
  // creditsOnHold above: no eleventh prop-sync effect, and the fetch below is
  // skipped entirely when initialCredits is seeded, so the prop is the only
  // path to an arm on those callers. Falls back to "control" — never the test
  // arm — when neither source has spoken.
  const [fetchedCardCopy, setFetchedCardCopy] = useState<CardCopyVariant | null>(
    null,
  );
  const cardCopyVariant =
    fetchedCardCopy ?? coerceCardCopyVariant(initialCardCopyVariant);
  const [loaded, setLoaded] = useState(initialCredits !== undefined);
  // Active $1 trial → the pool is the flat trial entitlement, not a monthly
  // plan allocation. Drives the "trial credits" wording so the chip never reads
  // like a paid monthly grant during the trial.
  const [trial, setTrial] = useState(false);
  // Billing interval from /api/credits. null = not known yet (or free), which
  // the credits modal must treat as "wait", never as monthly — see PANDA-2290.
  const [cadence, setCadence] = useState<"monthly" | "annual" | null>(null);
  // Upgrade-copy A/B arm, read from /api/credits alongside the plan. "control"
  // until it resolves, so a slow read shows today's copy rather than flashing.
  const [copyVariant, setCopyVariant] = useState<UpgradeCopyVariant>("control");
  const [agentTasksToday, setAgentTasksToday] = useState(initialAgentTasksToday ?? 0);
  const [agentDailyCap, setAgentDailyCap]     = useState(initialAgentDailyCap ?? 50);
  const [agentDailyResetsAt, setAgentDailyResetsAt] = useState<string | null>(
    initialAgentDailyResetsAt ?? null,
  );

  const [showCardModal, setShowCardModal] = useState(false);
  const [showRatedModal, setShowRatedModal] = useState(false);
  const [ratedStart, setRatedStart] = useState<Screen | undefined>(undefined);
  // Came back from Stripe without paying → reopen where they left off, with
  // their amount still selected. See lib/billing/checkout-intent-stash.
  const [ratedResumePlan, setRatedResumePlan] = useState<PaidPlanId | undefined>(
    undefined,
  );
  const [ratedResumeCredits, setRatedResumeCredits] = useState<
    number | undefined
  >(undefined);
  useCheckoutIntentResume(
    "rated",
    useCallback((resumed: CheckoutIntent) => {
      setRatedStart(resumed.screen as Screen);
      setRatedResumePlan(resumed.plan);
      setRatedResumeCredits(resumed.credits);
      setShowRatedModal(true);
    }, []),
  );

  // Sync from parent props when they change
  useEffect(() => {
    if (initialCredits !== undefined) setCredits(initialCredits);
  }, [initialCredits]);
  useEffect(() => {
    if (initialTotal !== undefined) setTotal(initialTotal);
  }, [initialTotal]);
  useEffect(() => {
    if (initialPlan !== undefined) setPlan(initialPlan);
  }, [initialPlan]);
  useEffect(() => {
    if (initialResetsAt !== undefined) setResetsAt(initialResetsAt);
  }, [initialResetsAt]);
  useEffect(() => {
    if (initialDailyUsed !== undefined) setDailyUsed(initialDailyUsed);
  }, [initialDailyUsed]);
  useEffect(() => {
    if (initialDailyLimit !== undefined) setDailyLimit(initialDailyLimit);
  }, [initialDailyLimit]);
  useEffect(() => {
    if (initialMonthlyAllocation !== undefined)
      setMonthlyAllocation(initialMonthlyAllocation);
  }, [initialMonthlyAllocation]);
  useEffect(() => {
    if (initialAgentTasksToday !== undefined) setAgentTasksToday(initialAgentTasksToday);
  }, [initialAgentTasksToday]);
  useEffect(() => {
    if (initialAgentDailyCap !== undefined) setAgentDailyCap(initialAgentDailyCap);
  }, [initialAgentDailyCap]);
  useEffect(() => {
    if (initialAgentDailyResetsAt !== undefined) setAgentDailyResetsAt(initialAgentDailyResetsAt);
  }, [initialAgentDailyResetsAt]);

  // Fetch credits if no initial values provided. Surfaces the free-tier daily
  // cap reset time (migration 131) so the replenishment moment is always clear.
  useEffect(() => {
    if (initialCredits !== undefined) return;
    fetch("/api/credits")
      .then((r) => {
        // MUST come before .json(). Every error this route returns is a JSON body
        // ({"error":"Failed to check credits"}), so without this check r.json()
        // RESOLVES on a 500/401/403/503 and the success branch below runs against
        // an error payload: every `typeof data.x === "number"` guard fails, no
        // state is written, and setLoaded(true) still fires — so the chip appears
        // showing its seed values as though they were the user's real balance.
        // The .catch below never saw those failures; it only covers transport and
        // parse errors. Throwing here routes them there, where they belong.
        if (!r.ok) throw new Error(`/api/credits ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (typeof data.credits_remaining === "number") setCredits(data.credits_remaining);
        if (typeof data.credits_total === "number") setTotal(data.credits_total);
        if (typeof data.rollover_credits === "number") setRollover(data.rollover_credits);
        if (data.plan) setPlan(data.plan);
        if (data.resets_at !== undefined) setResetsAt(data.resets_at);
        if (typeof data.daily_used === "number") setDailyUsed(data.daily_used);
        if (data.daily_limit !== undefined) setDailyLimit(data.daily_limit);
        if (typeof data.has_card === "boolean") setHasCard(data.has_card);
        setCardFirstTrial(
          data.card_required === true && data.card_first_trial
            ? (data.card_first_trial as CardFirstTrialTerms)
            : null,
        );
        if (typeof data.trial === "boolean") setTrial(data.trial);
        if (data.cadence === "monthly" || data.cadence === "annual")
          setCadence(data.cadence);
        // Assigned unconditionally (not guarded on truthiness) so a settled
        // build clears a previously-shown hold instead of leaving it stuck on.
        setFetchedOnHold(
          typeof data.credits_on_hold === "number" ? data.credits_on_hold : 0,
        );
        // Upgrade-copy A/B arm, from the same payload as the plan. Seeded into
        // the modal below so it opens on the correct copy instead of painting
        // the control arm and then swapping when its own status read lands.
        if (isUpgradeCopyVariant(data.upgrade_copy_variant))
          setCopyVariant(data.upgrade_copy_variant);
        // Card-copy A/B arm (GT-15), same payload, same first-frame reasoning.
        if (isCardCopyVariant(data.card_copy_variant))
          setFetchedCardCopy(data.card_copy_variant);
        if (data.monthly_allocation !== undefined)
          setMonthlyAllocation(
            typeof data.monthly_allocation === "number"
              ? data.monthly_allocation
              : null,
          );
        setLoaded(true);
        onCreditsChange?.(data.credits_remaining, data.plan, data.resets_at);
      })
      // Stay UNLOADED on failure. `if (!loaded) return null` below then renders
      // nothing at all, which is the only honest thing to show when we could not
      // read the balance — the alternative is the chip painting a confident
      // "0/50 credits" it invented. A credit surface that is absent is a surface
      // the user ignores; one that is wrong is one they act on.
      .catch(() => setLoaded(false));
  }, [initialCredits, onCreditsChange]);

  // Listen for credit changes from other parts of the app
  useEffect(() => {
    function handleCreditUpdate(
      e: CustomEvent<{
        credits_remaining: number;
        /** The pool CEILING. Optional because most dispatchers only know the
         *  balance moved — but a dispatcher that changed the PLAN must send it,
         *  or the chip renders the new balance over the old denominator. That
         *  is how a converted trial user's nav read "300/100 credits": the
         *  trial's 100 survived the event because it could not be carried. */
        credits_total?: number;
        plan?: string;
        resets_at?: string | null;
        daily_used?: number;
        daily_limit?: number | null;
        has_card?: boolean;
        credits_on_hold?: number;
        /** Active $1-trial flag. Drives the "trial credits" wording AND the
         *  chip's click routing, so a stale `true` sends a user who has just
         *  paid to leave the trial straight back to the convert modal. */
        trial?: boolean;
        monthly_allocation?: number | null;
      }>,
    ) {
      setCredits(e.detail.credits_remaining);
      // Undefined means "this dispatcher doesn't know about holds", which must
      // leave the current value alone rather than silently clearing a live one.
      if (typeof e.detail.credits_on_hold === "number")
        setFetchedOnHold(e.detail.credits_on_hold);
      if (typeof e.detail.credits_total === "number") setTotal(e.detail.credits_total);
      if (e.detail.plan) setPlan(e.detail.plan);
      if (e.detail.resets_at !== undefined) setResetsAt(e.detail.resets_at);
      if (typeof e.detail.daily_used === "number") setDailyUsed(e.detail.daily_used);
      if (e.detail.daily_limit !== undefined) setDailyLimit(e.detail.daily_limit);
      if (typeof e.detail.has_card === "boolean") setHasCard(e.detail.has_card);
      if (typeof e.detail.trial === "boolean") setTrial(e.detail.trial);
      if (e.detail.monthly_allocation !== undefined)
        setMonthlyAllocation(e.detail.monthly_allocation);
    }
    window.addEventListener("panda:credit-update", handleCreditUpdate as EventListener);
    return () => window.removeEventListener("panda:credit-update", handleCreditUpdate as EventListener);
  }, []);

  const warningFiredRef = useRef(false);

  const isFree = plan === "free";

  // Unused legacy counters (agent engine, rollover) — kept on the props/event
  // API for back-compat but not rendered by the chip.
  void agentTasksToday; void agentDailyCap; void agentDailyResetsAt;
  void warningFiredRef; void rollover;

  // Normalize the credit numbers once. For a free user inside the daily-cap
  // window (migration 131) this exposes the per-day allowance — what they can
  // ACTUALLY spend right now — separately from the banked total, so the chip
  // never implies all 293 remaining credits are usable today.
  const view = deriveCreditView({
    plan,
    credits_total: total,
    credits_remaining: credits,
    daily_limit: dailyLimit,
    daily_used: dailyUsed,
    daily_resets_at: resetsAt,
    monthly_allocation: monthlyAllocation,
    credits_on_hold: creditsOnHold,
  });
  // Non-null only while a build is holding credits. The chip is the surface a
  // user checks FIRST when a balance looks wrong, so it must be able to say the
  // dip is a reservation — in the same words as the builder's balance panel.
  const hold = formatCreditHold(view);
  // The canonical, single-source credit line — "50/50 credits" when full,
  // "38/50 credits · resets today at 11:53 PM" while the reset still helps, and
  // "2/300 credits · upgrade to keep building" once it doesn't. Same string on
  // desktop and mobile so the copy never diverges by viewport.
  const badge = formatCreditBadge(view);
  // Concise "· 650 credits/month" appended to the chip's tooltip so a paid user
  // can always see their subscription's monthly allocation on hover, without
  // widening the compact chip itself. null (free / unresolved) → omitted.
  const allocationLabel = formatMonthlyAllocation(view.monthlyAllocation);

  // Color logic keys off the relevant balance: the daily allowance for capped
  // free users (so it reddens when they're out for the day even with credits
  // banked), the total pool otherwise.
  const gaugeRemaining = view.isFreeDailyCapped
    ? view.availableToday
    : view.totalRemaining;
  const gaugeTotal = view.isFreeDailyCapped
    ? (view.dailyLimit ?? 0)
    : view.total;
  const usedPercent =
    gaugeTotal > 0 ? ((gaugeTotal - gaugeRemaining) / gaugeTotal) * 100 : 100;

  // Color logic: red when out, amber when low, default otherwise.
  let colorClass = "text-zinc-500 dark:text-zinc-400";
  let bgHoverClass = "hover:bg-zinc-100 dark:hover:bg-white/[0.06]";
  if (gaugeRemaining <= 0) {
    colorClass = "text-red-600 dark:text-red-400";
    bgHoverClass = "hover:bg-red-50 dark:hover:bg-red-950/30";
  } else if (usedPercent >= 80) {
    colorClass = "text-amber-600 dark:text-amber-400";
    bgHoverClass = "hover:bg-amber-50 dark:hover:bg-amber-950/30";
  }

  // Tooltip carries the full picture; the chip itself shows the canonical line.
  // Reset is hidden once the daily allowance is full (matches the chip). Paid
  // users get their monthly allocation appended so the grant is always visible.
  const baseLabel = trial
    ? `${credits} trial credits · expire when your trial ends`
    : view.isFreeDailyCapped
      ? `${view.availableToday} of ${view.dailyLimit} daily credits left · ${view.totalRemaining} of ${view.total} total${badge.detail ? ` · ${badge.detail}` : ""}`
      : `${credits} build credits${allocationLabel ? ` · ${allocationLabel}` : ""}`;
  // The tooltip is where the chip has room to explain a hold in full: what is
  // spendable now, what is reserved, and that the unused part comes back.
  const label = hold ? `${baseLabel} · ${hold.summary}` : baseLabel;

  // The chip is the most-seen credit surface, so it is where the copy-vs-ledger
  // tripwire is armed. Expected to be silent forever; if it isn't, the event
  // names the surface and both sides of the disagreement.
  const badgeText = badge.text;
  // Read off the string this component actually renders rather than a flag set
  // beside it: an edit that drops the hold copy flips this to false and trips
  // the tripwire, which is the only version of the check worth having.
  const holdDisclosed = hold == null || label.includes(hold.short);
  useEffect(() => {
    // Nothing to check while a purchase is confirming: the chip is deliberately
    // not making a credit claim, and the view it would be checked against is
    // the pre-purchase account the render is refusing to show.
    if (syncing) return;
    reportCreditCopyMismatches("nav_chip", view, badge, trackCreditCopyMismatch, {
      holdDisclosed,
    });
    // `badgeText` stands in for the whole (view, badge) pair: the badge is
    // derived from the view, so the rendered line changing is exactly when the
    // check needs to re-run. `holdDisclosed` is listed separately because a hold
    // can start or settle without the badge text moving at all. Re-running on
    // every render would only be noise — reportCreditCopyMismatches de-dupes,
    // but the work is pointless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [badgeText, holdDisclosed, view.onHold, syncing]);

  async function handleClick() {
    // A purchase is settling: `plan`, `credits` and `trial` are all still the
    // pre-purchase account, so every route out of here is decided on stale
    // facts — including offering full-price Pro to someone who has just bought
    // it, which is the second-charge shape PANDA-2292 recorded. Wait for it.
    if (syncing) return;
    if (cardFirstTrial) {
      setCardFirstOpen(true);
      return;
    }
    // Free-plan users with no remaining credits and no card on file must
    // complete the card-unlock flow before opening paid plans.
    if (isFree && credits <= 0 && !hasCard) {
      setShowCardModal(true);
      return;
    }
    // The credit chip is an upgrade entry point too — it renders on the
    // dashboard, the workspace and Settings, so before this it was one of the
    // most-clicked ways for an eligible free user to be shown full-price Pro.
    // `trial` is the ACTIVE-trial flag from /api/credits, not the offer: a
    // trialing user must be routed to plan management, never re-offered.
    const decision = await openUpgradeEntry({
      plan,
      trialActive: trial,
      entrypoint: "credit_chip",
      // The chip renders in three places and has no prop that names the screen.
      // `projectId` is the only honest discriminator available: inside a project
      // it is the workspace, otherwise the dashboard/Settings shell.
      entrySurface: projectId ? "workspace" : "dashboard",
      trigger: "user_click",
      projectId: projectId ?? null,
      creditsRemaining: credits,
    });
    if (decision.handled) return;
    setRatedStart(isRatedPaidPlan(plan) ? undefined : "chooseplan");
    setShowRatedModal(true);
  }

  if (!loaded) return null;

  return (
    <>
      <button
        onClick={handleClick}
        className={cn(
          "flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] font-medium transition-all duration-150",
          syncing ? "text-emerald-600 dark:text-emerald-400" : colorClass,
          bgHoverClass,
        )}
        title={syncing ? SYNCING_LABEL : cardFirstTrial ? "Start your free trial to build" : label}
      >
        <Coins
          className={cn("h-3.5 w-3.5 shrink-0", syncing && "animate-pulse")}
        />
        {/* MOBILE (< sm): the compact count only ("38/50") — no "credits" word,
            no reset clause — so the balance never wraps onto a second line or
            crowds the logo/avatar on a narrow iPhone. The reset time stays
            reachable in the chip's tooltip and in Settings → Billing.
            DESKTOP (>= sm): the full canonical line — "50/50 credits" when
            full; "38/50 credits · resets today at 11:53 PM" while a reset still
            adds credits; "2/300 credits · upgrade to keep building" once it
            doesn't. The detail is dimmed so the count stays the anchor. */}
        {/* A balance that is KNOWN to be stale is worse than no balance: it is
            the pre-purchase account presented as the current one. So while a
            purchase is confirming the chip says what is actually happening
            instead of a number it cannot stand behind. Self-clearing — the
            broadcast that ends the sync also carries the new balance. */}
        <span className="sm:hidden">
          {syncing ? SYNCING_SHORT : cardFirstTrial ? "Free trial" : badge.countShort}
        </span>
        <span className="hidden sm:inline">
          {syncing ? SYNCING_LABEL : cardFirstTrial ? "Start free trial" : badge.count}
          {!syncing && !cardFirstTrial && badge.detail ? (
            <span className="opacity-70"> · {badge.detail}</span>
          ) : null}
          {/* "· 50 on hold" — the shortest honest account of why the count just
              dropped. It takes priority over nothing (the reset/depletion
              clause keeps its place) and is dimmed the same way, so a build in
              flight is visible without the chip changing shape. The mobile
              variant stays the bare count by design (it has no room for a
              clause at all) and reaches the same explanation via the tooltip. */}
          {!syncing && !cardFirstTrial && hold ? (
            <span className="opacity-70"> · {hold.short}</span>
          ) : null}
        </span>
      </button>

      <CardEntryModal
        open={showCardModal}
        onClose={() => setShowCardModal(false)}
        projectId={projectId}
        creditsRemaining={credits}
        copyVariant={cardCopyVariant}
      />

      {cardFirstTrial && (
        <CardFirstTrialModal
          open={cardFirstOpen}
          terms={cardFirstTrial}
          onClose={() => setCardFirstOpen(false)}
          onStart={() => startCardFirstTrialCheckout("card_first_credit_chip")}
        />
      )}

      <RatedCreditsModal
        open={showRatedModal}
        onClose={() => {
          trackUpgradeDismissed(projectId ?? "", "");
          setShowRatedModal(false);
          setRatedStart(undefined);
          setRatedResumePlan(undefined);
          setRatedResumeCredits(undefined);
          // Closed on purpose → nothing left to reopen it later.
          clearCheckoutIntent();
        }}
        startScreen={ratedStart}
        initialPlan={ratedResumePlan}
        initialCredits={ratedResumeCredits}
        // No `returnTo`: the flow defaults to the page the user opened it from,
        // so a purchase started while browsing projects lands back there rather
        // than on Settings → Billing. Reconciling + confirming on an arbitrary
        // page is the root <CheckoutReturnHost/>'s job.
        // Seed from what the chip already knows → opens instantly, no loading.
        initialStatus={{
          plan,
          credits_remaining: credits,
          copy_variant: copyVariant,
          // `trial` is the ACTIVE-trial flag from /api/credits. Seeded so the
          // flow's first frame is trial-correct rather than resolving the buy
          // screen off a plan that reads 'pro' for trial accounts.
          is_trial: trial,
          // Only seeded once actually known; null keeps the modal on its
          // skeleton rather than guessing an interval it would have to correct.
          ...(cadence ? { cadence } : {}),
        }}
      />
    </>
  );
}

/** Dispatch a custom event to update credit counters across the app */
export function dispatchCreditUpdate(
  credits_remaining: number,
  plan?: string,
  resets_at?: string | null,
  daily_used?: number,
  daily_limit?: number | null,
  /** Credits an unsettled build is holding; omit when the caller can't know. */
  credits_on_hold?: number,
) {
  window.dispatchEvent(
    new CustomEvent("panda:credit-update", {
      detail: {
        credits_remaining,
        plan,
        resets_at,
        daily_used,
        daily_limit,
        credits_on_hold,
      },
    }),
  );
}
